import React from "react";
import {Box} from "@mantine/core";
import {IECGScanStep, IEEGScanStep, IMegScanStep} from "../../../types/protocol";
import {
    IECGScanStepDefinition,
    IEEGScanStepDefinition, IMegScanStepDefinition
} from "../../../types/protocolDefinitions";
import {StepDurationField} from "../fields/StepDurationField";
import {TagsInput} from "../../../../common/components/TagsInput";
import {FileBadge} from "../fields";
import {TSubjectId} from "../../../../common/types";
import classes from "../StepView.module.css";
import {DeepPartial} from "@/utils/types/DeepPartial";
import {TWorkflow} from "../StepView";

/** Scan step types for protocol execution mode */
export type TScanStep = IEEGScanStep | IECGScanStep | IMegScanStep;

/** Scan step definition types for protocol planning mode */
export type TScanStepDefinition = IEEGScanStepDefinition | IECGScanStepDefinition | IMegScanStepDefinition;

/** Combined type for all scan steps (execution and planning) */
export type TScanStepOrDefinition = TScanStep | TScanStepDefinition;

export interface ScanStepViewProps {
    step: TScanStepOrDefinition;
    subjectId?: TSubjectId;
    onChange: (updates: DeepPartial<TScanStepOrDefinition>) => void;
    workflow?: TWorkflow;
}

/**
 * View component for scan steps (MEG, EEG, ECG).
 * This is the primary rendering for scan steps in all contexts.
 * Used in protocol execution, planning, and review modes.
 */
export const ScanStepView: React.FC<ScanStepViewProps> = ({
    step,
    subjectId,
    onChange,
    workflow = "session",
}) => {
    const isReadOnly = workflow === "review";
    const isEditMode = workflow === "review_edit";

    return (
        <div style={{display: "flex", flexDirection: "column", width: "100%"}}>
            <Box style={{
                display: "grid",
                grid: "auto / auto 1fr auto",
                gridColumn: "1/-1",
                columnGap: "1em",
                alignItems: "end"
            }}>
                <div className={classes.durationField}>
                    <StepDurationField
                        duration={step.duration}
                        onChange={(duration) => onChange({duration})}
                        w={80}
                        disabled={isReadOnly}
                    />
                </div>
                <div className={classes.tagsField}>
                    <TagsInput
                        label="Tags"
                        placeholder="Add tags"
                        data-testid="input-tags"
                        value={step.tags || []}
                        setProps={({tags}: { tags: string[] }) => onChange({tags})}
                        size="xs"
                        disabled={isReadOnly}
                    />
                </div>
                <div className={classes.fileFieldRight}>
                    <FileBadge step={step} subjectId={subjectId} disabled={isReadOnly && !isEditMode}/>
                </div>
            </Box>
        </div>
    );
};